import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, CheckSquare } from 'lucide-react';
import { Card, CardContent, } from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import { Alert, AlertDescription, AlertTitle, } from '@avagency/frontend/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@avagency/frontend/components/ui/table';
import { EmptyState } from '@avagency/frontend/components/shell/EmptyState';
import { ErrorState } from '@avagency/frontend/components/shell/ErrorState';
import { LoadingState } from '@avagency/frontend/components/shell/LoadingState';
import { archiveContacts, excludeInactive, fetchInactive, fetchJobStatus, isTerminalStatus, } from '../api';
const stableIdempotencyKey = () => crypto.randomUUID();
const jobPollIntervalMs = 2000;
export function ReviewQueue({ integrationID }) {
    const qc = useQueryClient();
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['guardian', 'inactive', integrationID],
        queryFn: () => fetchInactive(integrationID),
    });
    const [selected, setSelected] = useState(new Set());
    const [pendingRequestID, setPendingRequestID] = useState(null);
    const [lastError, setLastError] = useState('');
    const jobStatusQuery = useQuery({
        queryKey: ['guardian', 'job-status', pendingRequestID],
        queryFn: () => fetchJobStatus(pendingRequestID),
        enabled: pendingRequestID !== null,
        refetchInterval: (q) => {
            if (q.state.status === 'error')
                return false;
            const data = q.state.data;
            if (!data)
                return jobPollIntervalMs;
            return isTerminalStatus(data.status) ? false : jobPollIntervalMs;
        },
        retry: 1,
    });
    useEffect(() => {
        const status = jobStatusQuery.data?.status;
        if (!status || !isTerminalStatus(status))
            return;
        qc.invalidateQueries({ queryKey: ['guardian', 'inactive', integrationID] });
        qc.invalidateQueries({ queryKey: ['guardian', 'archive-runs'] });
    }, [jobStatusQuery.data?.status, qc, integrationID]);
    const exclude = useMutation({
        mutationFn: excludeInactive,
        onSuccess: (resp) => {
            setSelected((prev) => {
                const next = new Set(prev);
                next.delete(resp.id);
                return next;
            });
            qc.invalidateQueries({ queryKey: ['guardian', 'inactive', integrationID] });
        },
    });
    const idempotencyKeyRef = useRef('');
    const archive = useMutation({
        mutationFn: () => archiveContacts(integrationID, [...selected], idempotencyKeyRef.current),
        onSuccess: (res) => {
            setPendingRequestID(res.request_id);
            setLastError('');
            setSelected(new Set());
            idempotencyKeyRef.current = '';
        },
        onError: (err) => setLastError(err.message),
    });
    const archiveClick = () => {
        if (!idempotencyKeyRef.current) {
            idempotencyKeyRef.current = stableIdempotencyKey();
        }
        archive.mutate();
    };
    if (isLoading)
        return _jsx(LoadingState, { rows: 6 });
    if (error)
        return _jsx(ErrorState, { error: error, retry: () => refetch() });
    const rows = data ?? [];
    const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
    const toggleAll = () => {
        if (allSelected) {
            setSelected(new Set());
        }
        else {
            setSelected(new Set(rows.map((r) => r.id)));
        }
    };
    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        setSelected(next);
    };
    return (_jsxs("div", { className: "hg-page-enter space-y-6", children: [_jsxs("div", { children: [_jsxs("h1", { className: "hg-page-title text-2xl font-semibold tracking-tight", children: ["Inactive contacts", rows.length > 0 && (_jsxs("span", { className: "ml-2 text-base font-normal text-muted-foreground", children: ["(", rows.length, " pending)"] }))] }), _jsx("p", { className: "mt-1 text-sm text-muted-foreground", children: "Contacts flagged as inactive by the weekly scan. Select to archive on demand, or exclude individual contacts from the next run." })] }), lastError && (_jsxs(Alert, { variant: "destructive", role: "alert", "aria-live": "assertive", children: [_jsx(AlertTitle, { children: "Archive failed" }), _jsx(AlertDescription, { children: lastError.toLowerCase().includes('fetch') || lastError.toLowerCase().includes('network')
                            ? "We couldn't reach the server. Please try again."
                            : lastError })] })), pendingRequestID && (_jsx(JobStatusBanner, { requestID: pendingRequestID, query: jobStatusQuery, onDismiss: () => setPendingRequestID(null) })), rows.length === 0 ? (_jsx(Card, { children: _jsx(CardContent, { className: "pt-6", children: _jsx(EmptyState, { icon: _jsx("div", { className: "hg-empty-ring", children: _jsx(Inbox, { className: "h-6 w-6 text-primary/70", "aria-hidden": "true" }) }), title: "You're all clear", description: "No inactive contacts flagged \u2014 your portal is in great shape. The next weekly scan runs Sunday at 02:00.", action: _jsxs("span", { className: "inline-flex items-center gap-1.5 text-xs text-muted-foreground/60", children: [_jsx(CheckSquare, { className: "h-3.5 w-3.5 text-green-500", "aria-hidden": "true" }), "All contacts active"] }) }) }) })) : (_jsx(Card, { children: _jsxs(CardContent, { className: "space-y-4 pt-6", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { variant: "outline", size: "sm", onClick: toggleAll, children: allSelected ? 'Clear selection' : 'Select all' }), _jsx(Button, { size: "sm", disabled: selected.size === 0 || archive.isPending, onClick: archiveClick, "aria-busy": archive.isPending, style: selected.size > 0 ? {
                                        boxShadow: '0 0 12px rgba(255,122,89,0.2)',
                                    } : undefined, children: archive.isPending
                                        ? 'Submitting…'
                                        : `Archive ${selected.size > 0 ? selected.size : ''} selected` }), selected.size > 0 && (_jsxs("span", { className: "text-xs text-muted-foreground ml-1", children: [selected.size, " contact", selected.size !== 1 ? 's' : '', " selected"] }))] }), _jsx("div", { className: "rounded-lg border border-border/60 overflow-hidden", children: _jsxs(Table, { children: [_jsx(TableHeader, { children: _jsxs(TableRow, { className: "hover:bg-transparent border-b border-border/60", children: [_jsx(TableHead, { className: "w-[48px]", children: _jsx("input", { type: "checkbox", "aria-label": allSelected ? 'Clear selection' : 'Select all contacts', checked: allSelected, onChange: toggleAll }) }), _jsx(TableHead, { className: "font-medium text-xs uppercase tracking-wider text-muted-foreground/70", children: "Email" }), _jsx(TableHead, { className: "font-medium text-xs uppercase tracking-wider text-muted-foreground/70", children: "Last activity" }), _jsx(TableHead, { className: "font-medium text-xs uppercase tracking-wider text-muted-foreground/70", children: "Scheduled archive" }), _jsx(TableHead, { className: "text-right font-medium text-xs uppercase tracking-wider text-muted-foreground/70", children: "Actions" })] }) }), _jsx(TableBody, { children: rows.map((r) => (_jsxs(TableRow, { className: "border-b border-border/40 last:border-0 cursor-pointer", onClick: () => toggleOne(r.id), children: [_jsx(TableCell, { onClick: (e) => e.stopPropagation(), children: _jsx("input", { type: "checkbox", "aria-label": `Select ${r.email || 'contact without email'}`, checked: selected.has(r.id), onChange: () => toggleOne(r.id) }) }), _jsx(TableCell, { className: "font-mono text-sm", children: r.email || (_jsx("span", { className: "text-muted-foreground/60 italic", children: "(no email)" })) }), _jsx(TableCell, { className: "text-sm text-muted-foreground tabular-nums", children: r.last_activity_at
                                                        ? new Date(r.last_activity_at).toLocaleDateString()
                                                        : _jsx("span", { className: "text-muted-foreground/40", children: "\u2014" }) }), _jsx(TableCell, { className: "text-sm text-muted-foreground tabular-nums", children: new Date(r.scheduled_archive_date).toLocaleDateString() }), _jsx(TableCell, { className: "text-right", onClick: (e) => e.stopPropagation(), children: _jsx(Button, { variant: "ghost", size: "sm", className: "text-xs text-muted-foreground hover:text-foreground", onClick: () => exclude.mutate(r.id), disabled: exclude.isPending, children: "Exclude" }) })] }, r.id))) })] }) })] }) }))] }));
}
function JobStatusBanner({ requestID, query, onDismiss }) {
    const shortID = requestID.slice(0, 8);
    if (query.isError) {
        return (_jsxs(Alert, { variant: "destructive", role: "alert", "aria-live": "assertive", children: [_jsx(AlertTitle, { children: "Couldn't check archive status" }), _jsxs(AlertDescription, { className: "flex items-center justify-between gap-3", children: [_jsxs("span", { children: ["We couldn't fetch the status for job ", shortID, "\u2026"] }), _jsx(Button, { variant: "ghost", size: "sm", onClick: onDismiss, children: "Dismiss" })] })] }));
    }
    const job = query.data;
    if (!job) {
        return (_jsx(Alert, { role: "status", "aria-live": "polite", children: _jsxs(AlertDescription, { children: ["Archive queued (request ", shortID, "\u2026). Checking status\u2026"] }) }));
    }
    let title;
    let detail = null;
    let variant = 'default';
    let ariaLive = 'polite';
    let role = 'status';
    switch (job.status) {
        case 'pending':
            title = `Archive queued`;
            detail = `Job ${shortID}… is waiting for the worker.`;
            break;
        case 'running':
            title = `Archiving contacts…`;
            detail = `Attempt ${job.retry_count + 1} of ${job.max_retries + 1}`;
            break;
        case 'done':
            title = `Archive complete`;
            detail = 'Contacts archived successfully. Refreshing history…';
            break;
        case 'failed':
            title = `Archive failed`;
            detail = job.last_result?.error
                ? `The worker reported: ${String(job.last_result.error)}`
                : `Job ${shortID}… failed after ${job.retry_count} attempt(s). Please try again.`;
            variant = 'destructive';
            ariaLive = 'assertive';
            role = 'alert';
            break;
        case 'cancelled':
            title = `Archive cancelled`;
            detail = null;
            break;
    }
    return (_jsxs(Alert, { variant: variant, role: role, "aria-live": ariaLive, children: [_jsx(AlertTitle, { children: title }), _jsxs(AlertDescription, { className: "flex items-center justify-between gap-3", children: [_jsx("span", { children: detail }), isTerminalStatus(job.status) && (_jsx(Button, { variant: "ghost", size: "sm", onClick: onDismiss, children: "Dismiss" }))] })] }));
}
